// FE-SCRM-002 — Sales CRM shell and navigation.
//
// Owns ``/dashboard/sales-crm``. Six tabs in the spec-defined order:
// Pipeline (default, will route to the Renewal Board once FE-SCRM-008
// lands) / Prospects / Contacts (FE-SCRM-053) / Activities / Reports /
// Settings. Tab state is stored in the ``?tab=`` query param so deep
// links and bookmarks survive a reload, matching the AccountDetailPage
// convention.
//
// Vocabulary discipline: every empty state speaks benefits-broker
// language. We never say "deal" or "task" — Opportunity, Prospect,
// and Sales Activity are the v1 nouns, and the Activities tab calls
// out the distinction from operational Commitments explicitly so
// producers don't conflate the two.
//
// Access is gated by ``PermissionGuard`` upstream in ``App.tsx``;
// this component assumes the user passed the gate.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { ActivitiesPanel as SalesActivitiesPanel } from "./activities";
import { ContactsListPanel } from "./Contacts";
import { ProspectsPanel as SalesProspectsPanel } from "./Prospects";
import { RenewalBoardPanel } from "./RenewalBoard";
import { ReportsPanel as SalesReportsPanel } from "./reports";
import { GlobalSearchModal } from "./search";
import { SettingsPanel as SalesSettingsPanel } from "./Settings";


type Tab =
  | "pipeline" | "prospects" | "contacts"
  | "activities" | "reports" | "settings";


const VALID_TABS: Tab[] = [
  "pipeline", "prospects", "contacts",
  "activities", "reports", "settings",
];


const TAB_LABEL: Record<Tab, string> = {
  pipeline: "Pipeline",
  prospects: "Prospects",
  contacts: "Contacts",
  activities: "Activities",
  reports: "Reports",
  settings: "Settings",
};


export function SalesCrmPage() {
  const [search, setSearch] = useSearchParams();
  const tab: Tab = useMemo(() => readTab(search.get("tab")), [search]);

  function selectTab(next: Tab) {
    const params = new URLSearchParams(search);
    params.set("tab", next);
    setSearch(params, { replace: true });
  }

  return (
    <div data-testid="sales-crm-page" className="flex flex-col gap-3">
      {/* FE-SCRM-064 (a): persistent page chrome. Title block + tab
          strip pin to the top of the layout's <main> scroll viewport
          so the page identity + tab navigation stay reachable as the
          panel (Pipeline toolbar, Renewal Board grid, etc.) scrolls
          underneath. Without sticky, the toolbar would scroll up
          past the chrome and the user would lose their bearings —
          producer screenshot of the built UI showed exactly this.
          The ``before:`` strip extends an opaque ``bg-canvas`` band
          24px above the sticky element so scrolling content can't
          bleed through <main>'s ``py-6`` padding region (same plug
          used on the Commitment Tracker's sticky toolbar). */}
      <div
        data-testid="sales-crm-chrome"
        className={[
          "sticky top-0 z-30 flex flex-col gap-2 bg-canvas pb-1",
          "before:pointer-events-none before:absolute",
          "before:bottom-full before:left-0 before:right-0",
          "before:h-6 before:bg-canvas before:content-['']",
        ].join(" ")}
      >
        {/* Single-line title — the previous two-line block with
            "Renewal Board" + "Sales CRM (Beta)" subtitle ate ~64px
            of vertical space above the grid. Sales CRM is the
            product name everywhere else in the app; the tab strip
            below already identifies "Pipeline" so the producer
            doesn't need a separate Renewal Board callout. The 2px
            teal accent stripe stays as the surface signal. */}
        <header
          data-testid="sales-crm-page-header"
          className="border-l-2 border-teal pl-3"
        >
          <h1
            data-testid="sales-crm-page-title"
            className="text-xl font-semibold tracking-tight text-text-primary"
          >
            Sales CRM
          </h1>
        </header>

        <Tabs currentTab={tab} onSelect={selectTab} />
      </div>

      {tab === "pipeline" && <PipelinePanel />}
      {tab === "prospects" && <ProspectsPanel />}
      {tab === "contacts" && <ContactsPanel />}
      {tab === "activities" && <ActivitiesPanel />}
      {tab === "reports" && <ReportsPanel />}
      {tab === "settings" && <SettingsPanel />}

      {/* FE-SCRM-054: ⌘+K opens the global-search overlay; the
          modal mounts itself and self-manages open/close. */}
      <GlobalSearchModal />
    </div>
  );
}


function readTab(raw: string | null): Tab {
  if (raw && (VALID_TABS as string[]).includes(raw)) return raw as Tab;
  return "pipeline";
}


function Tabs({
  currentTab, onSelect,
}: {
  currentTab: Tab;
  onSelect: (next: Tab) => void;
}) {
  return (
    <nav
      data-testid="sales-crm-tabs"
      role="tablist"
      className="flex gap-2 border-b border-border-subtle"
    >
      {VALID_TABS.map((t) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={currentTab === t}
          data-testid={`sales-crm-tab-${t}`}
          onClick={() => onSelect(t)}
          className={[
            "px-3 py-1.5 text-xs font-medium",
            currentTab === t
              ? "border-b-2 border-teal text-text-primary"
              : "text-text-secondary hover:text-text-primary",
          ].join(" ")}
        >
          {TAB_LABEL[t]}
        </button>
      ))}
    </nav>
  );
}


function PipelinePanel() {
  // FE-SCRM-008: the placeholder retired here; the Pipeline tab
  // now renders the live Renewal Board against the BE-SCRM-007
  // API. The placeholder copy that lived here is the spec narrative
  // for what the surface does, kept in the spec doc.
  return (
    <div data-testid="sales-crm-panel-pipeline">
      <RenewalBoardPanel />
    </div>
  );
}


function ProspectsPanel() {
  // FE-SCRM-049: the empty-state placeholder retired here; the
  // Prospects tab now renders the live list + AddProspectModal
  // against BE-SCRM-003 (CRUD) + BE-SCRM-018 (fit_score).
  return (
    <div data-testid="sales-crm-panel-prospects">
      <SalesProspectsPanel />
    </div>
  );
}


function ContactsPanel() {
  // FE-SCRM-053: new Contacts tab between Prospects and Activities.
  // The list is read-only entry-point only — new contacts are
  // created from a Prospect detail page (FE-SCRM-050).
  return (
    <div data-testid="sales-crm-panel-contacts">
      <ContactsListPanel />
    </div>
  );
}


function ActivitiesPanel() {
  // FE-SCRM-024: replaces the empty-state placeholder with the live
  // Activities surface. The placeholder copy distinguishing Sales
  // Activities from operational Commitments now lives in the panel's
  // copy + the empty list state inside ActivityListTable.
  return (
    <div data-testid="sales-crm-panel-activities">
      <SalesActivitiesPanel />
    </div>
  );
}


function ReportsPanel() {
  // FE-SCRM-058: Reports tab moves from 1 tile (StakeholderRisksTile
  // alone, mounted in FE-SCRM-013) to 5 — the new four wire the
  // previously-unwired BE-SCRM-012 / -025 / -026 / -027 endpoints, and
  // a page-level period selector cascades to every period-aware card.
  return (
    <div
      data-testid="sales-crm-panel-reports"
      className="flex flex-col gap-4"
    >
      <SalesReportsPanel />
    </div>
  );
}


function SettingsPanel() {
  // FE-SCRM-055: the placeholder retires here; the panel now renders
  // six sections (playbooks, workflow flags, import data, tag
  // library, broker-firm defaults, per-user prefs) on one scrolled
  // page. Composition lives in the Settings module so the panel
  // module stays a single import surface.
  return <SalesSettingsPanel />;
}
